import { FGStack, FGTypography } from '@fg/design-system';
import {
    InsightAndEventContainer,
    InsightAndEventLoadingContainer,
    InsightAndEventSeparator,
} from 'components/eventsFeed/EventDisplay';
import { IActiveCountry, IActivityRecord } from 'interfaces';
import { find, map } from 'lodash/fp';
import moment from 'moment';
import React, { useCallback, useMemo } from 'react';
import InsightTextEditor, { withInlines } from '../Insights/InsightTextEditor';
import { createEditor } from 'slate';
import { withHistory } from 'slate-history';
import { withReact } from 'slate-react';
import { withHtml } from '../Insights/SlateHTML';
import { CountryBadge } from 'components/eventsFeed/CountryBadge';
import ExternalInsightFrame from './ExternalInsightFrame';

type Props = {
    insight: IActivityRecord;
    isLoading: boolean;
    error: string;
    countriesById: { [countryID: number]: IActiveCountry };
};

const InsightDisplay = React.memo(
    ({ insight, isLoading, error, countriesById }: Props) => {
        const editor = useMemo(() => {
            return withInlines(
                withHtml(withHistory(withReact(createEditor())))
            );
        }, []);
    const getComponentById = useCallback(
        (id: string) => {
            return find(c => c.id === id, insight.components);
        },
        [insight]
    );
        if (isLoading) {
            return <InsightAndEventLoadingContainer />;
        }
        if (error) {
            return (
                <FGStack justifyContent="center" style={{ marginTop: 32 }}>
                    <FGTypography variant="headline4" tag="label" color="strong">
                        {error}
                    </FGTypography>
                </FGStack>
            );
        }
        return (
            <InsightAndEventContainer>
                <FGStack gap={4} style={{ width: 'min-content' }}>
                    {map(countryId => {
                        const countryData: IActiveCountry =
                            countriesById[countryId];
                        return (
                            <CountryBadge
                                country={countryData}
                                key={countryId}
                            />
                        );
                    }, insight?.countries)}
                </FGStack>
                <FGTypography
                    tag="h2"
                    variant="headline2"
                    color="strong"
                    cssStyle={{ marginBlock: 12 }}>
                    {insight?.insightsData.name}
                </FGTypography>
                <FGTypography tag="label" variant="body3-long" color="weak">
                    {moment(insight?.date).format('MMMM Do, YYYY')}
                </FGTypography>
                <InsightAndEventSeparator />
                {insight?.external_source === 'bmiResearch' ? (
                    <ExternalInsightFrame insight={insight} />
                ) : (
                    <InsightTextEditor
                        editor={editor}
                        showToolbar={false}
                        value={insight.insightsData.insight.nodes}
                        readOnly
                        getComponentById={getComponentById}
                    />
                )}
            </InsightAndEventContainer>
        );
    }
);

export default InsightDisplay;
